import { CheckIcon, Divider, MenuItem, Select, Stack, Typography } from '@customink/pigment-react';
import { SelectChangeEvent, Skeleton } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useSetQueryParam } from '~/adapters/browser/useSetQueryParam';
import { useUserAccounts } from '~/adapters/services/accounts/users/hooks';
import { useUserAccount } from '~/contexts/UserAccount/UserAccountContext';
import { CreateTeamButton } from '~/components/MultiAccountManagement/CreateTeamButton';
import * as Sentry from '@sentry/react';

export const useSwitchToPersonalAccount = () => {
    const setQueryParam = useSetQueryParam();
    return useCallback(() => {
        setQueryParam('accountId', undefined);
    }, [setQueryParam]);
};

const AccountSelector: React.FC = () => {
    const { account: currentAccount, user } = useUserAccount();
    const { data: accounts, isInitialLoading: isLoading } = useUserAccounts();
    const setQueryParam = useSetQueryParam();

    const [accountId, setAccountId] = useState<string>(currentAccount.id.toString());

    const handleSelectChange = useCallback(
        async (e: SelectChangeEvent) => {
            const { value: selectedAccountId } = e.target;
            setAccountId(selectedAccountId);
            const selectedAccount = accounts?.find((acc) => acc.id === +selectedAccountId);
            setQueryParam('accountId', selectedAccount?.personal ? undefined : selectedAccountId);
        },
        [accounts, setQueryParam],
    );

    if (isLoading) {
        return (
            <Skeleton width={200}>
                <CreateTeamButton />
            </Skeleton>
        );
    }

    return (
        <Typography variant="h5" sx={{ fontWeight: 400, pb: 3 }}>
            {(accounts ?? []).length > 1 ? (
                <Select
                    fullWidth
                    sx={{ maxWidth: 200 }}
                    value={accountId}
                    onChange={handleSelectChange}
                    renderValue={(selectedAccountId) => {
                        const selectedAccount = accounts?.find((acc) => acc.id === +selectedAccountId);
                        if (selectedAccount === undefined) {
                            Sentry.captureException(`User ${user.id} doesn't have account ${selectedAccountId}.`);
                            return null;
                        }
                        return selectedAccount?.displayName;
                    }}
                >
                    {accounts?.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                                {account.displayName}
                                {accountId === account.id.toString() && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
                            </Stack>
                        </MenuItem>
                    ))}
                    <Divider />
                    <CreateTeamButton variant="link" />
                </Select>
            ) : (
                // TODO @teichmaa fix this https://customink.monday.com/boards/5878886849/views/128588597/pulses/6069847021
                <CreateTeamButton />
            )}
        </Typography>
    );
};

export default AccountSelector;
